import { useEffect, useMemo, useRef, useState } from 'react';
import { MdExpandMore, MdSearch } from 'react-icons/md';
import { ALL_CURRENCIES, getCurrencyOption } from '../../lib/currency';
import { cn } from '../../lib/utils';

interface CurrencyPickerProps {
  value: string;
  onChange: (code: string) => void;
  /** Compact icon-only trigger (for conversion badge) */
  compact?: boolean;
  /** Full-width form control with the same currency list as Settings → Display */
  variant?: 'compact' | 'field';
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  align?: 'left' | 'right';
}

export function CurrencyPicker({
  value,
  onChange,
  compact = false,
  variant = 'compact',
  placeholder = 'SELECT',
  className,
  disabled = false,
  align = 'left',
}: CurrencyPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const isField = variant === 'field';

  const selected = getCurrencyOption(value) ?? (isField ? undefined : ALL_CURRENCIES[0]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_CURRENCIES;
    return ALL_CURRENCIES.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative', isField ? undefined : className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center font-bold text-[hsl(var(--foreground))] transition-colors',
          isField
            ? cn(className, 'justify-between gap-2 text-left')
            : cn(
                'gap-0.5 hover:bg-[hsl(var(--muted))]/50',
                compact
                  ? 'px-1.5 py-1 rounded text-sm'
                  : 'pl-3 pr-1.5 py-2.5 rounded-l-lg border-r border-[hsl(var(--border))] min-w-[3.25rem]',
              ),
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        aria-label={selected ? `Currency: ${selected.code}` : placeholder}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {isField ? (
          <span className={cn('truncate', !selected && 'text-[hsl(var(--muted-foreground))] font-semibold')}>
            {selected ? `${selected.code} (${selected.symbol}) — ${selected.name}` : placeholder}
          </span>
        ) : (
          <span className="text-base leading-none">{selected?.symbol}</span>
        )}
        <MdExpandMore className={cn('w-4 h-4 shrink-0 opacity-60', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-[200] mt-1 max-h-72 overflow-hidden flex flex-col rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl',
            isField ? 'left-0 w-full min-w-[16rem]' : 'w-64',
            !isField && (align === 'right' ? 'right-0' : 'left-0'),
          )}
        >
          <div className="p-2 border-b border-[hsl(var(--border))]">
            <div className="relative">
              <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search currency..."
                className="w-full pl-8 pr-2 py-2 text-sm rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                autoFocus
              />
            </div>
          </div>
          <ul className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">No currencies found</li>
            ) : (
              filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-[hsl(var(--muted))]/40 transition-colors',
                      c.code === value && 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]',
                    )}
                    onClick={() => {
                      onChange(c.code);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <span className="w-8 font-bold text-base">{c.symbol}</span>
                    <span className="flex-1">
                      <span className="font-semibold">{c.code}</span>
                      <span className="text-[hsl(var(--muted-foreground))] ml-2 text-xs">{c.name}</span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
